import { request } from './request';

// OpenAPI 访问令牌管理 (material-client 外部调用凭证).
// 明文 token 只在创建响应里出现一次, 之后只能看 tokenPrefix 掩码.

export interface ApiToken {
    id: number;
    uuid: string;
    name: string;
    tokenPrefix: string;
    ownerUid: number;
    ownerEmail: string;
    scope: string;
    /** 过期时间 unix 秒, 0 = 永不过期 */
    expiresAt: number;
    /** 最近调用时间 unix 秒, 0 = 从未使用 */
    lastUsedAt: number;
    /** pending = 申请待审批 (2026-09-28 起所有新令牌都要审批, 通过前调用一律 40101) */
    status: 'pending' | 'active' | 'revoked';
    rateQps: number;
    rateDaily: number;
    createdAt: number;
    updatedAt: number;
}

export interface CreatedToken {
    id: number;
    name: string;
    /** 明文, 仅创建响应返回这一次 */
    token: string;
    tokenPrefix: string;
    scope: string;
    /** pending = 已建审批工单, 审批通过后才能用; 本地 BYPASS 直接 active */
    status: ApiToken['status'];
    /** 审批工单号 (pending 时有值) */
    taskCode: string;
    expiresAt: number;
    createdAt: number;
}

const BASE = '/v1';

/**
 * scope: 省略/read=普通只读; sync=站点同步专用 (需站点同步编辑权); collector=商品采集增量写入 (需数据资产编辑权, 2026-09-14);
 * manage=以本人权限发起变更 (2026-09-24, 任何人可给自己签: 有效权限 = 本人权限去掉平台管理域与 approve, 永远不会超过本人).
 */
export function createApiToken(
    name: string,
    scope?: string,
): Promise<CreatedToken> {
    return request(`${BASE}/api-tokens`, {
        method: 'POST',
        body: { name, scope: scope || 'read' },
    });
}

/** 默认只返回自己的; all=true 需要 admin. */
export function listApiTokens(
    opts: { all?: boolean } = {},
): Promise<ApiToken[]> {
    return request(`${BASE}/api-tokens`, {
        query: { all: opts.all ? 'true' : '' },
    });
}

/** 吊销: 立即失效, 记录保留 (仍在列表展示"已吊销"). */
export function revokeApiToken(id: number): Promise<null> {
    return request(`${BASE}/api-tokens/${id}/revoke`, { method: 'POST' });
}

/** 删除记录 (软删, 列表不再展示). 仅已吊销的可删, active 必须先吊销. */
export function deleteApiToken(id: number): Promise<null> {
    return request(`${BASE}/api-tokens/delete`, {
        method: 'POST',
        body: { id },
    });
}

// ---- 调用统计 ----

export interface TokenDayStat {
    /** 20060102 */
    date: string;
    total: number;
    byStatus: Record<string, number>; // 2xx / 4xx / 429 / 5xx
}

export interface TokenStats {
    days: TokenDayStat[]; // 近 7 天, 老→新
    todayPaths: Record<string, number>; // 今日按接口分布
    todayIps: string[]; // 今日去重来源 IP
}

export interface TokenStatsResp {
    token: {
        id: number;
        name: string;
        tokenPrefix: string;
        rateQps: number;
        rateDaily: number;
    };
    stats: TokenStats;
}

/** 近 7 天调用统计, 本人或 admin 可查. */
export function getApiTokenStats(id: number): Promise<TokenStatsResp> {
    return request(`${BASE}/api-tokens/${id}/stats`);
}

/** admin: 调整单 token 限流参数. */
export function updateApiTokenRate(
    id: number,
    payload: { rateQps?: number; rateDaily?: number },
): Promise<null> {
    return request(`${BASE}/api-tokens/${id}/rate`, {
        method: 'PATCH',
        body: payload,
    });
}
